/**
 * Shared CASL ability.
 *
 * Single source of truth for authorization in this app. Consumed by BOTH
 * backend resolvers and frontend components. Any change in authz semantics
 * happens here, once.
 *
 * Semantics recap:
 *   - A user has `memberships`. Each membership is per-group and carries a
 *     list of roles. A role is (resourceType, actions).
 *   - Super-admin = holding the `group_admin` role on the `__root__` group
 *     in an active membership with an active role. Gets `manage:all`.
 *   - Everyone else gets fine-grained rules: one per (resourceType, action)
 *     combination present in their active roles, scoped by the groups where
 *     they hold that permission.
 *
 *   - Resources authz-check themselves by exposing a `groupIds: string[]`
 *     field. Empty / missing → no non-root rule matches → denied.
 *
 *   - Subject type detection: CASL uses `__typename` on objects returned by
 *     GraphQL to resolve them to a subject key. The mapping is shared with
 *     the frontend via TYPENAME_TO_SUBJECT.
 */
import {
  AbilityBuilder,
  createMongoAbility,
  type MongoAbility,
} from '@casl/ability';
import {
  type FlatAuthUser,
  type ResourceType,
  type RoleAction,
  ROOT_GROUP_NAME,
  SYSTEM_ROLE_NAMES,
  TYPENAME_TO_SUBJECT,
} from '@redirector/commons/types';

// ── CASL vocabulary ────────────────────────────────────────────────────────

export type AppSubject = ResourceType | 'all';
export type AppAction = RoleAction | 'manage';

export type AppAbility = MongoAbility<[AppAction, AppSubject]>;

// ── Subject type detection ─────────────────────────────────────────────────
//
// Handles four cases:
//   1. A string (already a subject key).
//   2. A GraphQL object with `__typename` → map to our lowercase subject.
//   3. A Mongoose document: look up `constructor.modelName`. Backend models
//      ('RedirectionConfig', 'User', 'Group') are named identically to the
//      GraphQL typenames, so the same map works.
//   4. Anything else → returned as-is; CASL falls back to its default.
export function detectSubjectType(subject: unknown): AppSubject | string {
  if (typeof subject === 'string') return subject;
  if (subject && typeof subject === 'object') {
    const tn = (subject as { __typename?: string }).__typename;
    if (tn && tn in TYPENAME_TO_SUBJECT) return TYPENAME_TO_SUBJECT[tn];
    const mn = (subject as { constructor?: { modelName?: string } }).constructor
      ?.modelName;
    if (mn && mn in TYPENAME_TO_SUBJECT) return TYPENAME_TO_SUBJECT[mn];
  }
  return subject as AppSubject;
}

// ── Root-admin detection ───────────────────────────────────────────────────

/**
 * True iff the user holds `group_admin` (resourceType=group) in the
 * `__root__` group via an ACTIVE membership and an ACTIVE role. This is
 * the sole super-admin signal, used both to short-circuit the ability
 * builder and as a client-side flag.
 */
export function isRootAdmin(
  user: Pick<FlatAuthUser, 'memberships'> | null | undefined,
): boolean {
  if (!user?.memberships) return false;
  return user.memberships.some((m) => {
    if (m.isDisabled) return false;
    if (m.groupName !== ROOT_GROUP_NAME) return false;
    return (m.roles ?? []).some(
      (r) =>
        !r.isDisabled &&
        r.name === SYSTEM_ROLE_NAMES.GROUP_ADMIN &&
        r.resourceType === 'group',
    );
  });
}

// ── Ability factory ────────────────────────────────────────────────────────

/**
 * Build a CASL ability from a user's memberships.
 *
 *   - Unauthenticated / empty user → ability that can do nothing.
 *   - Super-admin → `manage all`.
 *   - Otherwise → one rule per (resourceType, action) combination present in
 *     active roles, with a `groupIds: { $in: [...] }` condition scoping it
 *     to the groups where the user actually holds that permission.
 */
export function defineAbilityFor(user: FlatAuthUser | null | undefined): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  const finalize = () =>
    build({ detectSubjectType: detectSubjectType as never });

  if (!user?.memberships) return finalize();

  if (isRootAdmin(user)) {
    can('manage', 'all');
    return finalize();
  }

  // For each (resourceType, action) pair that ANY active role grants, collect
  // the set of groupIds where the user actually holds that permission.
  type Key = `${ResourceType}:${RoleAction}`;
  const groupsByPermission = new Map<Key, Set<string>>();

  for (const m of user.memberships) {
    if (m.isDisabled) continue;
    for (const role of m.roles ?? []) {
      if (role.isDisabled) continue;
      for (const action of role.actions) {
        const key: Key = `${role.resourceType}:${action}`;
        if (!groupsByPermission.has(key)) groupsByPermission.set(key, new Set());
        groupsByPermission.get(key)!.add(m.groupId);
      }
    }
  }

  // Emit one CASL rule per (resourceType, action).
  // CASL's `can` has a strict generic on conditions that disallows our
  // untyped condition literal. We cast the builder to a looser signature.
  const canLoose = can as unknown as (
    action: AppAction,
    subject: AppSubject,
    conditions: Record<string, unknown>,
  ) => void;

  for (const [key, groupIds] of groupsByPermission) {
    const [resourceType, action] = key.split(':') as [ResourceType, RoleAction];
    const ids = [...groupIds];
    // A Group IS a group; authz keys on its own id. Every other resource
    // links to groups via a `groupIds` array. We use `id` (string) because
    // it is stable on BOTH a Mongoose document (virtual getter returning
    // the stringified _id) and a shaped GraphQL payload. This avoids the
    // ObjectId-vs-string comparison pitfalls of sift's `$in`.
    const conditions =
      resourceType === 'group'
        ? { id: { $in: ids } }
        : { groupIds: { $in: ids } };
    canLoose(action, resourceType, conditions);
  }

  return finalize();
}

/** Empty ability (can do nothing). */
export function createEmptyAbility(): AppAbility {
  return createMongoAbility<[AppAction, AppSubject]>([], {
    detectSubjectType: detectSubjectType as never,
  });
}
